import type React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { faBriefcaseMedical } from '@fortawesome/pro-solid-svg-icons/faBriefcaseMedical';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons/faShieldHalved';
import { faUserCrown } from '@fortawesome/pro-solid-svg-icons/faUserCrown';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SegmentedControl, useMantineTheme } from '@mantine/core';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './dropin-menu.module.less';
import messages from './messages';
import { CookieService } from '../../services/cookie-service';
import {
  identifyLdUser,
  getStoredLdContextMode,
  setStoredLdContextMode,
  LdContextMode,
  SYSTEM_ADMIN_SELLER_ID,
} from '../../feature-flags/identify-ld-user';
import { startDropin, endDropin, setEnableAllFeatures } from '../../store/admin/admin.actions';
import Global from '../../store/global-selectors';

const DropinMenu = () => {
  const [dropinSeller, setDropinSeller] = useState('');
  const [ldContextMode, setLdContextMode] = useState(getStoredLdContextMode);
  const seller = CookieService.getCookieValue('x-imp');
  const currentSellerId = useSelector(Global.selectSellerSellerId);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { primaryColor } = useMantineTheme();
  const isAllFeaturesEnabled = useSelector(Global.selectAdminIsAllFeaturesEnabled);

  const updateSeller = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDropinSeller(e.target.value);
  }, []);

  const handleFeaturesChange = useCallback(
    (value: string) => {
      dispatch(setEnableAllFeatures(value === 'all'));
    },
    [dispatch],
  );

  const activeSellerId = seller ?? currentSellerId;

  const handleLdContextChange = useCallback(
    (value: string) => {
      setStoredLdContextMode(value);
      setLdContextMode(value);
      const sellerId = value === LdContextMode.SELLER ? activeSellerId : SYSTEM_ADMIN_SELLER_ID;
      identifyLdUser(sellerId).catch(noop);
    },
    [activeSellerId],
  );

  const hasSeller = seller !== undefined && seller !== '';

  useEffect(() => {
    if (!seller) return;
    const storedMode = getStoredLdContextMode();
    if (storedMode === LdContextMode.ADMIN) {
      identifyLdUser(SYSTEM_ADMIN_SELLER_ID).catch(noop);
    }
  }, [seller]);

  return (
    <DropdownMenu className={style['admin-menu-dropdown']} closeWhenChildrenClicked={false} menuPosition='bottom-right'>
      <DropdownMenuToggler className={style['dropped-in-button']} hideChevron theme='unstyled'>
        <div className={classnames({ [style['dropped-in']]: hasSeller })}>
          <span className={style['icon-wrapper']}>
            <FontAwesomeIcon icon={faBriefcaseMedical} />
          </span>
          {hasSeller ? <div>{seller}</div> : null}
          <span className={style['icon-wrapper']}>
            {isAllFeaturesEnabled ? <FontAwesomeIcon icon={faUserCrown} /> : <FontAwesomeIcon icon={faUserGroup} />}
          </span>
        </div>
      </DropdownMenuToggler>
      <div className={classnames(style['admin-menu-container'], style['menu-container'])}>
        <div className={style['input-controls']}>
          <div className={style['input-field-wrap']}>
            <input
              onChange={updateSeller}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              placeholder={intl.formatMessage(messages.dropinInputPlaceholder)}
              value={dropinSeller}
            />
          </div>
          <div className={style['input-button-wrap']}>
            <div className='button-group'>
              <Button onClick={() => dispatch(startDropin(dropinSeller))} theme={THEME_CONSTANTS.tertiary}>
                <FormattedMessage {...messages.dropIn} />
              </Button>
            </div>
          </div>
        </div>
        <div className={style.informational}>
          <FormattedMessage {...messages.dropinExplanation} />
        </div>
        <Text textType={TextType.H4} value={messages.dropinMode} />
        <SegmentedControl
          color={primaryColor}
          data={[
            {
              label: (
                <span>
                  <FontAwesomeIcon icon={faUserGroup} /> Seller&apos;s Features
                </span>
              ),
              value: 'seller',
            },
            {
              label: (
                <span>
                  <FontAwesomeIcon icon={faUserCrown} /> All Features
                </span>
              ),
              value: 'all',
            },
          ]}
          fullWidth
          onChange={handleFeaturesChange}
          value={isAllFeaturesEnabled ? 'all' : 'seller'}
        />
        <Text textType={TextType.H4} value={messages.ldContextMode} />
        <SegmentedControl
          color={primaryColor}
          data={[
            {
              label: (
                <span>
                  <FontAwesomeIcon icon={faFlag} /> Seller
                </span>
              ),
              value: LdContextMode.SELLER,
            },
            {
              label: (
                <span>
                  <FontAwesomeIcon icon={faShieldHalved} /> Admin
                </span>
              ),
              value: LdContextMode.ADMIN,
            },
          ]}
          fullWidth
          onChange={handleLdContextChange}
          value={ldContextMode}
        />
        <hr />
        <div className={style['footer-action']}>
          <Button onClick={() => dispatch(endDropin())} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.stopDropIn} />
          </Button>
        </div>
      </div>
    </DropdownMenu>
  );
};

export default DropinMenu;
